import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Table } from 'reactstrap';
import { fetchSliderFaqs } from '../../../actions/sliderFaqAction';
import { getSliderFaqError, isSelectableSliderFaq } from '../../../helpers/sliderFaq';
import useDebounce from '../../../reusables/hooks/useDebounce';
import TblBody from '../../../components/Tbl/TblBody';
import Paginations from '../../../components/Paginations';

const FAQ_PAGE_SIZE = 15;

// Mount only while open so each selection session starts with a fresh, scoped list.
export function ModalFaqs({ fetchFaqs, onSelect, toggle }) {
  const [keyword, setKeyword] = useState('');
  const [language, setLanguage] = useState('');
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const debouncedKeyword = useDebounce(keyword, 300);

  useEffect(() => {
    setLoading(true);
    setError('');
    if (keyword !== debouncedKeyword) return;
    const source = axios.CancelToken.source();
    let current = true;
    const load = async () => {
      try {
        const data = await fetchFaqs({
          keyword: debouncedKeyword,
          language_code: language,
          page,
          per_page: FAQ_PAGE_SIZE,
          sort_by: 'updatedAt_desc',
        }, source.token);
        if (!current) return;
        if (!data || !Array.isArray(data.data) || !Number.isFinite(data.total)) {
          throw new Error('Invalid FAQ list response');
        }
        setResult(data);
      } catch (failure) {
        if (current && !axios.isCancel(failure)) setError(getSliderFaqError(failure));
      } finally {
        if (current) setLoading(false);
      }
    };
    load();
    return () => {
      current = false;
      source.cancel();
    };
  }, [fetchFaqs, keyword, debouncedKeyword, language, page, retry]);

  const pending = loading || keyword !== debouncedKeyword;
  const rows = result ? result.data : [];
  return (
    <Modal isOpen toggle={toggle} size="lg" labelledBy="slider-faq-title">
      <ModalHeader toggle={toggle} id="slider-faq-title">Choose a FAQ</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label htmlFor="slider-faq-search">Question</Label>
          <Input id="slider-faq-search" value={keyword} placeholder="Search FAQ questions..."
            onChange={event => { setKeyword(event.target.value); setPage(1); }} />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="slider-faq-language">Language</Label>
          <Input id="slider-faq-language" type="select" value={language}
            onChange={event => { setLanguage(event.target.value); setPage(1); }}>
            <option value="">All languages</option>
            <option value="ind">Indonesian (ind)</option>
            <option value="eng">English (eng)</option>
          </Input>
        </FormGroup>
        <p className="text-muted">Only active FAQs in active categories can be selected.</p>
        <div aria-live="polite">
          {error ? (
            <Alert color="danger">
              {error}{' '}
              <Button type="button" size="sm" onClick={() => setRetry(value => value + 1)}>Retry</Button>
            </Alert>
          ) : (
            <Table responsive>
              <thead><tr><th>Question</th><th>Category</th><th>Language</th><th>Action</th></tr></thead>
              <TblBody isLoad={pending} hasRow={rows.length > 0} columnCount={4} emptyRowText="No matching FAQs found.">
                {rows.map(faq => (
                  <tr key={faq.id}>
                    <td>{faq.question}<div className="text-muted">ID: {faq.id}</div></td>
                    <td>{faq.category ? faq.category.name : 'Unavailable category'}</td>
                    <td>{faq.language_code}</td>
                    <td>
                      <Button type="button" size="sm" color="primary" disabled={!isSelectableSliderFaq(faq)}
                        onClick={() => onSelect(faq)}>Select</Button>
                      {!isSelectableSliderFaq(faq) && <small className="d-block text-muted">FAQ or category unavailable</small>}
                    </td>
                  </tr>
                ))}
              </TblBody>
            </Table>
          )}
        </div>
        {!pending && !error && result && result.total > 0 && (
          <Paginations total={result.total} perPage={result.per_page} currentPage={result.current_page}
            lastPage={result.last_page} maxPage={8} handleGotoCallback={setPage} />
        )}
      </ModalBody>
    </Modal>
  );
}

export default connect(null, { fetchFaqs: fetchSliderFaqs })(ModalFaqs);
